import {SingletonService} from '@/common/dependency.configs'
import {isObject} from 'lodash'
import {injectable, inject} from 'inversify'
import {TabConstants} from '@/common/constant/TabConstants'
import FACTORY_MSG from '@/common/messages/factory.messages'
import type IWfState from '@/common/models/WfState'
import StartWorkflowFactory from '@/common/services/Workflow/StartWorkflowFactory'
import WorkflowStateFactory from '@/common/services/Workflow/workflowStateFactory'
import LogService from '@/common/services/Log/LogService'
import {WorkflowGroupStore} from '@/branchmanager/app/workspace/workflowGroupStore.ts'
import EmployeeChannelWorkflowGroupsFactory from '@/common/services/Workflow/EmployeeChannelWorkflowGroupsFactory.ts'
import {
  getLdClient,
  waitForLdReady
} from '@/branchmanager/config/ldClientRef.ts'

@SingletonService
@injectable()
class TabFactory {
  constructor(
    @inject(StartWorkflowFactory)
    private startWorkflowFactory: StartWorkflowFactory,
    @inject(WorkflowStateFactory)
    private workflowStateFactory: WorkflowStateFactory,
    @inject(LogService)
    private logService: LogService,
    @inject(EmployeeChannelWorkflowGroupsFactory)
    private employeeChannelWorkflowGroupsFactory: EmployeeChannelWorkflowGroupsFactory
  ) {}

  getStateList(tabList: IWfState[]): string[] {
    return tabList.map((tab: {state: string}) => {
      return tab.state
    })
  }

  enableAllTabs(tabList: IWfState[]): void {
    tabList.forEach((tab: {disabled: boolean}) => {
      tab.disabled = false
    })
  }

  updateTabValidation(tabList: IWfState[], validationList: IWfState[]): void {
    tabList.forEach((tab) => {
      const matchingValidation = validationList.filter((validation) => {
        return validation.state === tab.state
      })[0]

      if (isObject(matchingValidation)) {
        tab.warnings = matchingValidation.warnings || []
        tab.errors = matchingValidation.errors || []
        tab.infos = matchingValidation.infos || []
      } else {
        this.logService.error(FACTORY_MSG.TAB.NO_MATCHING_VALIDATION.replace('{tabState}', tab.state))
      }
    })
  }

  disableAfterError(tabList: IWfState[]): void {
    tabList.forEach((tab, index) => {
      if (tab.errors?.length > 0 && isObject(tabList[index + 1])) {
        tabList[index + 1].disabled = true
      }
      if (index !== 0 && tabList[index - 1].disabled === true) {
        tab.disabled = true
      }
    })
  }

  getStartWorkflowCode(
    stateType: 'applicant' | 'enrollment',
    workspaceType: 'PERSON' | 'BUSINESS'
  ) {
    return waitForLdReady()
      .then(() => {
          return getLdClient()?.variation('open-anywhere-employee-channel-custom-workflows', false)
            ? this.getStartWorkflowCodeEnhanced(stateType, workspaceType)
            : this.getStartWorkflowCodeLegacy(stateType, workspaceType)
        })
  }

  getStartWorkflowCodeLegacy(
    stateType: 'applicant' | 'enrollment',
    workspaceType: 'PERSON' | 'BUSINESS'
  ) {
    let startWorkflowCode = ''

    switch (stateType) {
      case 'enrollment':
        startWorkflowCode =
          workspaceType === TabConstants.PERSON
            ? TabConstants.BRANCH_PERSON_ENROLLMENT
            : TabConstants.BRANCH_BUSINESS_ENROLLMENT
        break
      case 'applicant':
        startWorkflowCode =
          workspaceType === TabConstants.PERSON
            ? TabConstants.BRANCH_PERSON_INFO
            : TabConstants.BRANCH_BUSINESS_INFO
        break
    }

    return Promise.resolve(startWorkflowCode)
  }

  getStartWorkflowCodeEnhanced(
    stateType: 'applicant' | 'enrollment',
    workspaceType: 'PERSON' | 'BUSINESS'
  ) {
    const workflowGroupId = WorkflowGroupStore().workspaceGroup?.workflowGroupId

    if (!workflowGroupId) {
      return this.getStartWorkflowCodeLegacy(stateType, workspaceType)
    }

    return this.employeeChannelWorkflowGroupsFactory
      .get(workflowGroupId)
      .then((response) => {
        switch (stateType) {
          case 'enrollment':
          default:
            return response.data.payload.enrollmentWorkflow
          case 'applicant':
           return workspaceType === TabConstants.PERSON
                ? response.data.payload.applicantWorkflow
                : response.data.payload.businessRepresentativeWorkflow
        }
      })
  }

  getTabsFromWorkflow(
    stateType: 'applicant' | 'enrollment',
    workspaceType: 'PERSON' | 'BUSINESS'
  ): Promise<IWfState[]> {
    return this.getStartWorkflowCode(stateType, workspaceType).then(
      (startWorkflowCode) => {
        return this.startWorkflowFactory
          .get(startWorkflowCode ?? '')
          .then((response) => {
            return this.workflowStateFactory
              .getByQuery(response.workflowName, {params: {orderby: 'linear'}})
              .then((workflow: IWfState[]) => workflow)
          })
      }
    )
  }
}
export default TabFactory
